// * MUI
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';

// * Icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';

// * Images
// TODO: move to a standalone component
import averageDark from '../../../../assets/average_dark.png';
import averageLight from '../../../../assets/average_light.png';
import baselineDark from '../../../../assets/baseline_dark.png';
import baselineLight from '../../../../assets/baseline_light.png';
import initiativeDark from '../../../../assets/initiative_dark.png';
import initiativeLight from '../../../../assets/initiative_light.png';
import comparisonDark from '../../../../assets/post_init_dark.png';
import comparisonLight from '../../../../assets/post_init_light.png';
import projectedDark from '../../../../assets/projected_dark.png';
import projectedLight from '../../../../assets/projected_light.png';

// * i18n
import { useTranslation } from 'react-i18next';

// * Queries
import { useGetEnergySavingsQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { usePutEnergyInitiativeMutation } from '../../../../api/recalcV2/endpoints/energyInitiativesApiSlice';

// * Components
import DeleteInitiativeDialog from '@/features/energyxplorer/components/initiatives/DeleteInitiativeDialog';
import InitiativeChartDrawer from '@/features/energyxplorer/components/initiatives/InitiativeChartDrawer';
import InitiativeDataGrid from '@/features/energyxplorer/components/initiatives/InitiativeDataGrid';
import InitiativeKanban from '@/features/energyxplorer/components/initiatives/InitiativeKanban';

// * Redux
import CardHeaderWithIcon from '@/components/card/CardHeaderWithIcon';
import CreateInitiativeWithPlotDialog from '@/features/energyxplorer/components/initiatives/CreateInitiativeWithPlotDialog';
import EditInitiativeWithPlotDialog from '@/features/energyxplorer/components/initiatives/EditInitiativeWithPlotDialog';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { useSelector } from 'react-redux';

const readCo2eGrams = (branch) => {
  if (!branch) return 0;
  const sum = branch.CO2eSavingsSummary || branch.Co2eSavingsSummary;
  if (!sum) return 0;
  const v = sum.Co2eSavings ?? sum.CO2eSavings; // try both casings
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// prefer the flagged branch, then fall back to the other; convert g → kg
const getCO2eSavingsKg = (initiative) => {
  const s = initiative?.energySavings;
  if (!s) return 0;

  const preferred = s.AdvancedModelUsed
    ? readCo2eGrams(s.AdvancedEnergySavings)
    : readCo2eGrams(s.BasicEnergySavings);

  if (preferred) return preferred / 1000;

  const fallback = s.AdvancedModelUsed
    ? readCo2eGrams(s.BasicEnergySavings)
    : readCo2eGrams(s.AdvancedEnergySavings);

  return fallback / 1000;
};

const getEstimatedCO2eSavingsKg = (initiative) => {
  const s = initiative?.energySavings;
  if (!s) return 0;
  const adv = s.ForecastedSavings?.AdvEstimatedTotalSavingsCO2e / 1000;
  const bas = s.ForecastedSavings?.BasicEstimatedTotalSavingsCO2e / 1000;

  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const STATES = ['Created', 'Initialized', 'Active', 'Done', 'Archived'];

// Extract energy savings in kWh using your rules
function getEnergySavingsKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;

  const adv = s.AdvancedEnergySavings?.EnergySavingsSummary?.EnergySavings;
  const bas = s.BasicEnergySavings?.EnergySavingsSummary?.EnergySavings;

  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getActualEnergyUsageKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;

  const adv = s.AdvancedEnergySavings?.EnergySavingsSummary?.ActualEnergyUsage;
  const bas = s.BasicEnergySavings?.EnergySavingsSummary?.ActualEnergyUsage;
  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getEstimatedSavingsToDateKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;
  const adv = s.ForecastedSavings?.AdvEstimatedTotalSavings;
  const bas = s.ForecastedSavings?.BasicEstimatedTotalSavings;

  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function getEstimatedSavingsYearlyKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;
  const adv =
    s.AdvancedEnergySavings?.ForecastedSavings?.AdvEstimatedYearlySavings;
  const bas =
    s.BasicEnergySavings?.ForecastedSavings?.BasicEstimatedYearlySavings;
  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Generic grouper: sums a value getter by initiative.state
function sumByState(initiatives, valueGetter) {
  const acc = {
    Created: 0,
    Initialized: 0,
    Active: 0,
    Done: 0,
    Archived: 0,
  };

  for (const i of initiatives || []) {
    const raw = i?.state || '';
    const state = STATES.find(
      (s) => s.toLowerCase() === String(raw).toLowerCase(),
    );
    if (!state) continue; // skip unknown/missing states
    acc[state] += valueGetter(i);
  }

  return acc;
}

// Public helpers
function getEnergySavingsByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEnergySavingsKWh);
}

function getActualEnergyUsageByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getActualEnergyUsageKWh);
}

function getEstimatedSavingsToDateByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEstimatedSavingsToDateKWh);
}

function getEstimatedSavingsYearlyByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEstimatedSavingsYearlyKWh);
}

function getCO2eSavingsByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getCO2eSavingsKg);
}

function getEstimatedCO2eSavingsToDateKg(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEstimatedCO2eSavingsKg);
}

// Format helper (round before Intl)
const fmt0 = (n) => new Intl.NumberFormat('da-DK').format(Math.round(n || 0));

const InitiativesTab = ({ selectedEnergyGroup }) => {
  const account = useSelector(getSelectedAccount);
  const {
    data: energySavingsData,
    isLoading: isLoadingEnergySavingsData,
    isFetching: isFetchingEnergySavingsData,
  } = useGetEnergySavingsQuery(
    {
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      accountId: selectedEnergyGroup?.accountId,
    },
    {
      skip: !selectedEnergyGroup?.energyGroupId,
    },
  );

  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const steps = [
    {
      label: t('common.baseline_period'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={baselineLight}
          alt="Baselineperioden"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={baselineDark}
          alt="Baselineperioden"
        />
      ),
    },
    {
      label: t('common.initiative'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={initiativeLight}
          alt="Initiativet"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={initiativeDark}
          alt="Initiativet"
        />
      ),
    },
    {
      label: t('common.comparison_period'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={comparisonLight}
          alt="Sammenligning"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={comparisonDark}
          alt="Sammenligning"
        />
      ),
    },
    {
      label: t('common.projected'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={projectedLight}
          alt="Projekteret"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={projectedDark}
          alt="Projekteret"
        />
      ),
    },
    {
      label: t('common.average'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={averageLight}
          alt="Gennemsnit"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={averageDark}
          alt="Gennemsnit"
        />
      ),
    },
  ];

  const getStepDescription = (step) => {
    return t(`step_descriptions.${step}`);
  };

  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [putEnergyInitiative] = usePutEnergyInitiativeMutation();

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const [openCreate, setOpenCreate] = useState(false);
  const handleCreateInitiative = () => {
    setOpenCreate(true);
  };

  const [openEdit, setOpenEdit] = useState(false);
  const handleEditInitiative = (initiative) => {
    if (!initiative) return;
    setSelectedInitiative(initiative);
    setOpenEdit(true);
  };

  const [openDelete, setOpenDelete] = useState(false);
  const handleDeleteInitiative = (initiative) => {
    if (!initiative) return;
    setSelectedInitiative(initiative);
    setOpenDelete(true);
  };

  const [openPlot, setOpenPlot] = useState(false);

  const handleViewInitiative = (initiative) => {
    setSelectedInitiative(initiative);
    setOpenPlot(true);
  };

  const [showKanban, setShowKanban] = useState(false);
  const [initiativeStateFilter, setInitiativeStateFilter] =
    useState('Archived');

  const theme = useTheme();

  const handleCloseInitiativePlotDialog = () => {
    setOpenPlot(false);
    setSelectedInitiative(null);
  };

  const enrichedInitiatives = useMemo(() => {
    const initiatives = selectedEnergyGroup?.energyInitiatives || [];
    const savings = energySavingsData?.InitiativeEnergySavings || [];

    // Create a lookup map from InitiativeEnergySavings
    const savingsMap = new Map(savings.map((s) => [s.Id, s]));

    // Merge matching savings data into each initiative
    return initiatives.map((initiative) => {
      const savingsData = savingsMap.get(initiative.energyInitiativeId);
      return {
        ...initiative,
        energySavings: savingsData || null,
      };
    });
  }, [selectedEnergyGroup, energySavingsData?.InitiativeEnergySavings]);

  const handleCloseHelpDialog = () => {
    setHelpDialogOpen(false);
    setActiveStep(0);
  };

  const savingsByState = useMemo(
    () => getEnergySavingsByState(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const actualEnergyUsageByState = useMemo(
    () => getActualEnergyUsageByState(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const estimatedSavingsToDateByState = useMemo(
    () => getEstimatedSavingsToDateByState(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const estimatedCO2eSavingsToDateByState = useMemo(
    () => getEstimatedCO2eSavingsToDateKg(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const estimatedSavingsYearlyByState = useMemo(
    () => getEstimatedSavingsYearlyByState(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const co2eByState = useMemo(
    () => getCO2eSavingsByState(enrichedInitiatives),
    [enrichedInitiatives],
  );

  const initiativeCount = useMemo(() => {
    let archivedCount = 0;
    let activeCount = 0;
    let createdCount = 0;

    (enrichedInitiatives || []).forEach((i) => {
      const state = i?.state || '';
      const s = state.toLowerCase();
      if (s === 'archived') archivedCount++;
      else if (s === 'active' || s === 'done') activeCount++;
      else if (s === 'created' || s === 'initialized') createdCount++;
    });
    return { archivedCount, activeCount, createdCount };
  }, [enrichedInitiatives]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="end"
        alignItems="center"
        gap={1}
        sx={{ mb: 2 }}
      >
        {/* {!showKanban && (
          <Button
            variant="outlined"
            onClick={() => setShowKanban(true)}
            startIcon={<ViewKanbanRoundedIcon />}
            sx={{
              borderColor: 'divider',
              ':hover': { borderColor: 'divider' },
            }}
          >
            {t('button_labels.show_kanban')}
          </Button>
        )} */}
        {showKanban && (
          <Button
            variant="outlined"
            onClick={() => setShowKanban(false)}
            startIcon={<TableChartRoundedIcon />}
            sx={{
              borderColor: 'divider',
              ':hover': { borderColor: 'divider' },
            }}
          >
            {t('button_labels.show_table')}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={
            <AddRoundedIcon
              sx={{ transition: 'margin-right 0.1s ease-in-out' }}
            />
          }
          onClick={handleCreateInitiative}
        >
          {t('button_labels.add_initiative')}
        </Button>
      </Box>
      {enrichedInitiatives.length !== 0 && !showKanban && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <>
            <Grid item xs>
              <Card
                variant="outlined"
                sx={{
                  padding: 2,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  initiativeStateFilter === 'Archived'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Archived')
                }
              >
                <CardHeaderWithIcon
                  icon={<FolderRoundedIcon />}
                  tooltip={t('tooltips.report_ready_card')}
                  title={t('cards.report_ready_card.title')}
                  dimmed={
                    initiativeStateFilter !== null &&
                    initiativeStateFilter !== 'Archived'
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{
                      opacity:
                        initiativeStateFilter !== null &&
                        initiativeStateFilter !== 'Archived'
                          ? 0.2
                          : 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                    gutterBottom
                  >
                    {initiativeCount.archivedCount}{' '}
                    {t('cards.report_ready_card.caption')}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="textSecondary"
                    gutterBottom
                    sx={{
                      opacity:
                        initiativeStateFilter !== null &&
                        initiativeStateFilter !== 'Archived'
                          ? 0.2
                          : 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  >
                    {t('cards.report_ready_card.subtitle')}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={'space-between'}
                  >
                    {isLoadingEnergySavingsData ||
                    isFetchingEnergySavingsData ? (
                      <Skeleton variant="text" width="100%" height={40} />
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="baseline"
                        gap={1}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            opacity:
                              initiativeStateFilter !== null &&
                              initiativeStateFilter !== 'Archived'
                                ? 0.2
                                : 1,
                            transition: 'opacity 0.3s ease-in-out',
                          }}
                        >
                          {fmt0(estimatedSavingsToDateByState?.Archived)} kWh
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            opacity:
                              initiativeStateFilter !== null &&
                              initiativeStateFilter !== 'Archived'
                                ? 0.2
                                : 1,
                            transition: 'opacity 0.3s ease-in-out',
                          }}
                        >
                          / {fmt0(estimatedCO2eSavingsToDateByState?.Archived)}{' '}
                          kg CO₂e
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardHeaderWithIcon>
              </Card>
            </Grid>

            <Grid item xs>
              <Card
                variant="outlined"
                height="100%"
                sx={{
                  padding: 2,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  initiativeStateFilter === 'Done'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Done')
                }
              >
                <CardHeaderWithIcon
                  icon={<QueryStatsRoundedIcon />}
                  tooltip={t('tooltips.active_card')}
                  title={t('cards.active_card.title')}
                  dimmed={
                    initiativeStateFilter !== null &&
                    initiativeStateFilter !== 'Done'
                  }
                />
                <Typography
                  variant="caption"
                  sx={{
                    opacity:
                      initiativeStateFilter !== null &&
                      initiativeStateFilter !== 'Done'
                        ? 0.2
                        : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  gutterBottom
                >
                  {initiativeCount.activeCount > 0
                    ? `${initiativeCount.activeCount} ${t('cards.active_card.caption')}`
                    : 'Ingen initiativer kræver handling.'}
                </Typography>

                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="textSecondary"
                  gutterBottom
                  sx={{
                    opacity:
                      initiativeStateFilter !== null &&
                      initiativeStateFilter !== 'Done'
                        ? 0.2
                        : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                >
                  {t('cards.active_card.subtitle')}
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{
                        opacity:
                          initiativeStateFilter !== null &&
                          initiativeStateFilter !== 'Done'
                            ? 0.2
                            : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                    >
                      {fmt0(
                        actualEnergyUsageByState?.Done +
                          actualEnergyUsageByState?.Active,
                      )}{' '}
                      kWh
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>

            <Grid item xs>
              <Card
                variant="outlined"
                height="100%"
                sx={{
                  padding: 2,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  initiativeStateFilter === 'Created'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Created')
                }
              >
                <CardHeaderWithIcon
                  icon={<AutoAwesomeRoundedIcon />}
                  tooltip={t('tooltips.started_card')}
                  title={t('cards.started_card.title')}
                  dimmed={
                    initiativeStateFilter !== null &&
                    initiativeStateFilter !== 'Created'
                  }
                />
                <Typography
                  variant="caption"
                  sx={{
                    opacity:
                      initiativeStateFilter !== null &&
                      initiativeStateFilter !== 'Created'
                        ? 0.2
                        : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                >
                  {initiativeCount.createdCount > 0
                    ? `${initiativeCount.createdCount} ${t('cards.started_card.caption')}`
                    : 'Ingen initiativer kræver handling.'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="textSecondary"
                  gutterBottom
                  sx={{
                    opacity:
                      initiativeStateFilter !== null &&
                      initiativeStateFilter !== 'Created'
                        ? 0.2
                        : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                >
                  {t('cards.started_card.subtitle')}
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                >
                  {isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
                    <Skeleton variant="text" width="100%" height={40} />
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{
                        opacity:
                          initiativeStateFilter !== null &&
                          initiativeStateFilter !== 'Created'
                            ? 0.2
                            : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                    >
                      {fmt0(
                        actualEnergyUsageByState?.Initialized +
                          actualEnergyUsageByState?.Created,
                      )}{' '}
                      kWh
                    </Typography>
                  )}
                </Stack>
              </Card>
            </Grid>
          </>
        </Grid>
      )}

      {!showKanban && (
        <InitiativeDataGrid
          initiatives={enrichedInitiatives}
          energyGroup={selectedEnergyGroup}
          handleViewInitiative={handleViewInitiative}
          handleEditInitiative={handleEditInitiative}
          handleDeleteInitiative={handleDeleteInitiative}
          isLoading={isLoadingEnergySavingsData || isFetchingEnergySavingsData}
          initiativeStateFilter={initiativeStateFilter}
        />
      )}

      <CreateInitiativeWithPlotDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        energyGroup={selectedEnergyGroup}
      />

      <EditInitiativeWithPlotDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        energyGroup={selectedEnergyGroup}
        initiative={selectedInitiative}
      />

      <DeleteInitiativeDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        energyGroup={selectedEnergyGroup}
        initiative={selectedInitiative}
      />

      {/* // TODO: make this into its own component */}
      <Dialog
        open={helpDialogOpen}
        onClose={handleCloseHelpDialog}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>{t('step_descriptions.title')}</DialogTitle>
        <DialogContent dividers>
          <>
            <Box sx={{ mx: 16, mt: 4 }}>
              <Stepper activeStep={activeStep}>
                {steps.map((step, index) => {
                  const stepProps = {};
                  const labelProps = {};
                  return (
                    <Step key={step.label} {...stepProps}>
                      <StepLabel
                        {...labelProps}
                        StepIconProps={{
                          sx: {
                            '&.Mui-completed': {
                              color: 'success.main',
                            },
                            '&.Mui-active': {
                              color: 'primary.main',
                              scale: 1.2,
                              transition: 'transform 0.3s ease-in-out',
                            },
                          },
                        }}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              <Box
                sx={{
                  display: 'flex',
                  align: 'center',
                  flexDirection: 'column',
                  pt: 2,
                }}
              >
                {activeStep === steps.length ? (
                  <React.Fragment>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      <Typography>{t('step_descriptions.default')}</Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}
                    >
                      <Button onClick={handleReset}>
                        {t('step_descriptions.reset')}
                      </Button>
                    </Box>
                  </React.Fragment>
                ) : (
                  <>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
                    >
                      {theme.palette.mode === 'dark'
                        ? steps[activeStep].imgDark
                        : steps[activeStep].img}
                      <Typography
                        sx={{ mt: 2, mb: 1 }}
                        variant="body1"
                        color="textSecondary"
                      >
                        {getStepDescription(activeStep)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}
                    >
                      <IconButton
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                      >
                        <ArrowBackRoundedIcon />
                      </IconButton>

                      <IconButton
                        color="inherit"
                        disabled={activeStep === steps.length}
                        onClick={handleNext}
                        sx={{ ml: 1 }}
                      >
                        <ArrowForwardRoundedIcon />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </>
        </DialogContent>
      </Dialog>

      {openPlot && (
        <InitiativeChartDrawer
          open={openPlot}
          onClose={handleCloseInitiativePlotDialog}
          selectedInitiative={selectedInitiative}
          updateInitiative={putEnergyInitiative}
        />
      )}

      {showKanban && (
        <InitiativeKanban
          initiatives={enrichedInitiatives}
          handleEditInitiative={handleEditInitiative}
          handleDeleteInitiative={handleDeleteInitiative}
          handleViewInitiative={handleViewInitiative}
          estimatedSavingsToDateByState={estimatedSavingsToDateByState}
          estimatedSavingsYearlyByState={estimatedSavingsYearlyByState}
          savingsByState={savingsByState}
          co2eByState={co2eByState}
          actualEnergyUsageByState={actualEnergyUsageByState}
          energyGroup={selectedEnergyGroup}
          isLoading={isLoadingEnergySavingsData || isFetchingEnergySavingsData}
          account={account}
        />
      )}
    </>
  );
};

export default InitiativesTab;
